import Paper from '@material-ui/core/Paper';
import { makeStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import Button from '@material-ui/core/Button';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Typography from '@material-ui/core/Typography';
import ContactSupportOutlinedIcon from '@material-ui/icons/ContactSupportOutlined';
import { useDispatch } from 'react-redux';
import React, { useEffect, useState } from 'react';
import {
  deleteInput,
  getEc2Instances,
  getIVSChannelsInfo,
  getMediaLiveChannelsInfo,
  listInputs,
  getSessions,
} from 'utils/apiCalls';
import EC2Row from './components/ec2TableRow';
import IVSRow from './components/ivsTableRow';
import MLRow from './components/mlTableRow';
import SessionsTable from './components/SessionsTable';
import CircularProgress from '@material-ui/core/CircularProgress';
import useInterval from 'hooks/useInterval';
import { presenterStatus } from 'utils/apiCalls';
import { setNotificationAction } from 'store/utils/utilActions';
import sortBy from 'lodash/sortBy';

const useStyles = makeStyles(() => ({
  root: {
    marginTop: '1rem',
  },
  tabContainer: {
    margin: '2rem',
    padding: '1rem',
  },
}));

export default function Admin(props) {
  const classes = useStyles(props);
  const [tab, setTab] = useState(0);
  const [ec2Info, setEc2Info] = useState({});
  const [mlInfo, setMLInfo] = useState({});
  const [mlInputs, setMLInputs] = useState({});
  const [ivsInfo, setIVSInfo] = useState({});
  const [liveSessions, setLiveSessions] = useState([]);
  const [readHelp, setReadHelp] = useState({});
  const [firstRead, setFirstRead] = useState(true);
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();

  useEffect(() => {
    const calls = async () => {
      setLoading(true);
      setLiveSessions((await getSessions()).Sessions);
      setEc2Info(await getEc2Instances());
      setMLInfo(await getMediaLiveChannelsInfo());
      setIVSInfo(await getIVSChannelsInfo());
      setMLInputs(await listInputs());
      setLoading(false);
    };
    calls();
  }, []);

  const refreshData = async () => {
    setLoading(true);
    setLiveSessions((await getSessions()).Sessions);
    setEc2Info(await getEc2Instances());
    setMLInfo(await getMediaLiveChannelsInfo());
    setIVSInfo(await getIVSChannelsInfo());
    setMLInputs(await listInputs());
    setLoading(false);
  };

  const getUpdatedAlerts = async () => {
    if (!liveSessions || !liveSessions.length) return;

    const newReadHelp = { ...readHelp };
    const newLiveSessions = [...liveSessions];
    let helpRequests, helpId, changeRead, data, session, sessionData, sessionId;

    try {
      for (const index in liveSessions) {
        session = liveSessions[index];
        if (session.Status === 'LIVE') {
          sessionId = session.SessionId;
          data = await presenterStatus(sessionId);
          sessionData = data?.session;
          if (sessionData) {
            helpRequests = sessionData.HelpRequest;
            if (helpRequests) {
              for (const request of helpRequests) {
                helpId = `${request.TimeStamp}-${request.UserId}`;

                if (!readHelp[helpId]) {
                  newReadHelp[helpId] = changeRead = true;
                  newLiveSessions[index].HelpRequest = helpRequests;
                  if (!firstRead) {
                    dispatch(
                      setNotificationAction({
                        message: `Help Request: ${request.Message} (in session ${sessionId})`,
                        type: 'warning',
                        icon: ContactSupportOutlinedIcon,
                        duration: 30000,
                      })
                    );
                  }
                }
              }
            }
          }
        }
      }
    } catch (e) {
      console.error(e);
    }

    if (changeRead) {
      setReadHelp(newReadHelp);
      setLiveSessions(newLiveSessions);
    }
    setFirstRead(false);
  };

  useInterval(() => {
    getUpdatedAlerts();
  }, 10000);

  return (
    <div className={classes.root}>
      <Paper
        style={{
          flexGrow: 1,
        }}
      >
        <Typography variant="h4" gutterBottom component="div" style={{ textAlign: 'center' }}>
          Admin panel
        </Typography>
        <div style={{ textAlign: 'right', marginRight: '2rem' }}>
          {loading ? (
            <CircularProgress />
          ) : (
            <Button color="primary" variant="contained" onClick={() => refreshData()}>
              Refresh
            </Button>
          )}
        </div>
      </Paper>

      <Paper
        style={{
          flexGrow: 1,
        }}
      >
        <Tabs
          value={tab}
          onChange={(event, newTab) => setTab(newTab)}
          indicatorColor="primary"
          textColor="primary"
          centered
        >
          <Tab label="Live Sessions" />
          <Tab label="EC2" />
          <Tab label="Media Live" />
          <Tab label="IVS" />
        </Tabs>
      </Paper>

      <Paper className={classes.tabContainer}>
        {tab === 0 && (
          <SessionsTable
            sessions={sortBy(liveSessions, (e) => {
              return e.Status;
            })}
          />
        )}
        {tab === 1 && (
          <>
            <TableContainer component={Paper}>
              <Table aria-label="collapsible table">
                <TableHead>
                  <TableRow>
                    <TableCell />
                    <TableCell>Name</TableCell>
                    <TableCell align="right">Status</TableCell>
                    <TableCell align="right">Id</TableCell>
                    <TableCell align="right">Resource Id</TableCell>
                    <TableCell align="right"></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ec2Info?.instances?.Reservations?.map((row) =>
                    row.Instances?.[0]?.State?.Name !== 'terminated' ? (
                      <EC2Row key={row.name} row={row.Instances[0]} />
                    ) : (
                      ''
                    )
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
        {tab === 2 && (
          <>
            <TableContainer component={Paper}>
              <Table aria-label="collapsible table">
                <TableHead>
                  <TableRow>
                    <TableCell />
                    <TableCell>Name</TableCell>
                    <TableCell align="right">Status</TableCell>
                    <TableCell align="right">Inputs</TableCell>
                    <TableCell align="right">Destinations</TableCell>
                    <TableCell align="right"></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mlInfo?.data?.Channels?.map((row) => (
                    <MLRow key={row.name} row={row} />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            Input Lists
            <TableContainer component={Paper}>
              <Table aria-label="collapsible table">
                <TableHead>
                  <TableRow>
                    <TableCell>Input Name</TableCell>
                    <TableCell align="right">Id</TableCell>
                    <TableCell align="right">Source</TableCell>
                    <TableCell align="right">State</TableCell>
                    <TableCell align="right">Channel ID</TableCell>
                    <TableCell align="right">Type</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mlInputs?.data?.Inputs?.map((input) => {
                    return (
                      <TableRow className={classes.root} key={input.Id}>
                        <TableCell>{input.Name}</TableCell>
                        <TableCell align="right">{input.Id}</TableCell>
                        <TableCell align="right">
                          {input.Type === 'RTMP_PUSH'
                            ? input.Destinations?.[0].Url
                            : input.Sources?.[0].Url}
                        </TableCell>
                        <TableCell align="right">{input.State}</TableCell>
                        <TableCell align="right">{input.AttachedChannels}</TableCell>
                        <TableCell align="right">{input.Type}</TableCell>
                        <TableCell align="right">
                          <button onClick={() => deleteInput(input.Id)}>Delete</button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
        {tab === 3 && (
          <>
            <TableContainer component={Paper}>
              <Table aria-label="collapsible table">
                <TableHead>
                  <TableRow>
                    <TableCell />
                    <TableCell>Name</TableCell>
                    <TableCell align="right">Status</TableCell>
                    <TableCell align="right">Url</TableCell>
                    <TableCell align="right">Ingest Endpoint</TableCell>
                    <TableCell align="right">Viewers</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ivsInfo?.data?.map((row) => (
                    <IVSRow key={row.name} row={row} />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Paper>
    </div>
  );
}
